import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowRight, ArrowLeft, CheckCircle2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { BRAND } from '../../lib/brand';
import { logger } from '../../lib/logger';

const STEPS = ['preferenze', 'budget', 'contatti'];

/**
 * Modulo 2 — qualification wizard (3 steps).
 *
 * Mobile-first layout (2026-05 fix):
 *   - The page is a full-height flex column.
 *   - HEADER (logo + title + progress bar) is sticky to the top of the
 *     viewport, so the user always sees which step they're on.
 *   - The FORM CONTENT scrolls inside the available space between header
 *     and footer (no extra outer scroll, no buttons "in mezzo" cutting the
 *     view on small screens).
 *   - The FOOTER (Indietro / Continua / Invia) is sticky to the bottom so
 *     the primary action is always reachable without scrolling past the
 *     form. On desktop the same layout works fine (header/footer remain
 *     in place; form area takes the natural height).
 */
export default function Modulo2Page() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    destination_preference: '', investment_type: '', objective: '',
    budget: '', horizon: '', funds_source: '',
    name: '', email: '', phone: '', consent_privacy: true,
  });

  const update = (k, v) => setForm({ ...form, [k]: v });

  const next = () => {
    if (step === 0 && !form.investment_type) { toast.error('Scegli un percorso'); return; }
    if (step === 1 && !form.budget) { toast.error('Indica un budget'); return; }
    setStep(step + 1);
  };

  const submit = async () => {
    if (!form.name || !form.email) { toast.error('Nome ed email sono obbligatori'); return; }
    setSubmitting(true);
    try {
      const r = await api.post('/public/modulo2', form);
      setDone(r.data);
      toast.success('Modulo inviato! Ti reindirizziamo al passo successivo.');
      setTimeout(() => nav(r.data.next_step_url || '/vsl/initial'), 1800);
    } catch (e) {
      logger.error('Modulo2Page: submit failed', e);
      toast.error('Errore nell’invio. Riprova.');
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-[var(--surface-1)]">
        <Card className="max-w-lg w-full border-border" data-testid="modulo2-done-card">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-[hsl(var(--primary))] mx-auto" />
            <div className="font-serif text-2xl mt-3 text-[hsl(var(--primary))]">Profilo registrato</div>
            <p className="text-sm text-muted-foreground mt-2">Ti accompagniamo al video del tuo percorso.</p>
            <div className="mt-3 inline-flex items-center gap-2 text-xs text-muted-foreground">
              <Sparkles className="w-3 h-3" /> AI Score: {done.analysis?.score} / Branch: {done.branch}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    // dvh = dynamic viewport height — accounts for mobile browser chrome
    // collapsing/expanding (Safari toolbar etc.). Falls back to vh.
    <div
      className="bg-[var(--surface-2)] flex flex-col"
      style={{ minHeight: '100dvh', height: '100dvh' }}
      data-testid="qualification-wizard"
    >
      {/* ─── STICKY HEADER ─────────────────────────────────────────────── */}
      <header
        className="sticky top-0 z-20 bg-[var(--surface-2)]/95 backdrop-blur border-b border-border px-4 pt-4 pb-3 md:pt-6 md:pb-5"
        data-testid="modulo2-header"
      >
        <div className="max-w-2xl mx-auto">
          <div className="flex flex-col items-center text-center">
            <img
              src={BRAND.greenParadiseLogoSmall}
              alt="Green Paradise"
              className="h-10 md:h-14 w-auto mb-2 md:mb-3"
            />
            <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground">
              HCF Property
            </div>
            <h1 className="font-serif text-xl md:text-3xl lg:text-4xl text-[hsl(var(--primary))] mt-1 md:mt-2 leading-tight">
              Trova il tuo percorso
            </h1>
            <p className="hidden md:block text-sm text-muted-foreground mt-1">
              60 secondi. Per indirizzarti correttamente.
            </p>
          </div>
          <div className="h-1 mt-3 md:mt-5 rounded-full bg-secondary overflow-hidden" aria-hidden="true">
            <div
              className="h-full bg-[hsl(var(--primary))] rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
              data-testid="modulo2-progress"
            />
          </div>
        </div>
      </header>

      {/* ─── SCROLLABLE FORM CONTENT ───────────────────────────────────── */}
      <main
        className="flex-1 overflow-y-auto px-3 py-4 md:px-4 md:py-6"
        data-testid="modulo2-scroll-area"
      >
        <div className="max-w-2xl mx-auto">
          <Card className="border-border">
            <CardContent className="p-4 md:p-8">
              {step === 0 && (
                <div className="space-y-5">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Step 1 / 3</div>
                    <div className="font-serif text-xl mt-0.5">Preferenze</div>
                  </div>
                  <div className="space-y-2">
                    <Label>Quale percorso ti interessa?</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {[
                        { v: 'equity', t: 'Equity Estate', d: 'Operazione strutturata. Distribuzioni periodiche, exit pianificato (scenari indicativi nei documenti).' },
                        { v: 'real_estate', t: 'Real Estate', d: 'Acquisto immobile con verifica sul posto.' },
                        { v: 'unsure', t: 'Non sono sicuro', d: 'Aiutatemi voi a scegliere.' },
                      ].map((opt) => (
                        <button
                          key={opt.v}
                          type="button"
                          onClick={() => update('investment_type', opt.v)}
                          data-testid={`modulo2-investment-${opt.v}`}
                          className={`text-left rounded-lg border p-4 transition-colors ${form.investment_type === opt.v ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))]' : 'border-border bg-card hover:border-[hsl(var(--primary))]/40'}`}
                        >
                          <div className="font-medium text-sm">{opt.t}</div>
                          <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{opt.d}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Destinazione preferita</Label>
                    <div className="flex flex-wrap gap-2">
                      {['Lombok', 'Spille', 'Thailandia', 'Zanzibar', 'Indifferente'].map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => update('destination_preference', d)}
                          className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${form.destination_preference === d ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))]' : 'border-border bg-card hover:border-[hsl(var(--primary))]/40'}`}
                          data-testid={`modulo2-dest-${d}`}
                        >{d}</button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Obiettivo principale</Label>
                    <Input
                      value={form.objective}
                      onChange={(e) => update('objective', e.target.value)}
                      placeholder="Es: rendita strutturata, casa al mare, diversificazione"
                      data-testid="modulo2-objective"
                    />
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-5">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Step 2 / 3</div>
                    <div className="font-serif text-xl mt-0.5">Budget &amp; tempistiche</div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Budget indicativo</Label>
                    <div className="flex flex-wrap gap-2">
                      {['fino a 100k', '100-300k', '300-500k', '500k+'].map((b) => (
                        <button
                          key={b}
                          type="button"
                          onClick={() => update('budget', b)}
                          className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${form.budget === b ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))]' : 'border-border bg-card hover:border-[hsl(var(--primary))]/40'}`}
                          data-testid={`modulo2-budget-${b}`}
                        >{b}</button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Orizzonte temporale</Label>
                    <div className="flex flex-wrap gap-2">
                      {['Subito', '3 mesi', '6 mesi', '12 mesi'].map((b) => (
                        <button
                          key={b}
                          type="button"
                          onClick={() => update('horizon', b)}
                          className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${form.horizon === b ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))]' : 'border-border bg-card hover:border-[hsl(var(--primary))]/40'}`}
                          data-testid={`modulo2-horizon-${b}`}
                        >{b}</button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Provenienza fondi (opzionale)</Label>
                    <Input
                      value={form.funds_source}
                      onChange={(e) => update('funds_source', e.target.value)}
                      placeholder="Liquidità, vendita immobile, finanziamento…"
                      data-testid="modulo2-funds"
                    />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Step 3 / 3</div>
                    <div className="font-serif text-xl mt-0.5">Contatti</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Nome e cognome*</Label>
                      <Input
                        value={form.name}
                        onChange={(e) => update('name', e.target.value)}
                        data-testid="modulo2-name"
                        autoComplete="name"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Email*</Label>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={(e) => update('email', e.target.value)}
                        data-testid="modulo2-email"
                        autoComplete="email"
                        inputMode="email"
                      />
                    </div>
                    <div className="space-y-1.5 md:col-span-2">
                      <Label>Telefono / Telegram</Label>
                      <Input
                        value={form.phone}
                        onChange={(e) => update('phone', e.target.value)}
                        placeholder="+39…"
                        data-testid="modulo2-phone"
                        autoComplete="tel"
                        inputMode="tel"
                      />
                    </div>
                  </div>
                  <label className="flex items-start gap-2 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={form.consent_privacy}
                      onChange={(e) => update('consent_privacy', e.target.checked)}
                      data-testid="modulo2-consent"
                    />
                    <span>Acconsento al trattamento dei dati personali ai sensi del GDPR e alla ricezione di comunicazioni di Green Paradise / HCF Property.</span>
                  </label>
                  <div className="text-xs text-muted-foreground bg-[var(--surface-2)] border border-border rounded-md p-3">
                    <b>Risposta entro 24h.</b> I tuoi dati sono protetti, mai condivisi con terze parti.
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          {/* Bottom spacer so the last form row never sits behind the
              sticky footer when the keyboard is open on mobile. */}
          <div aria-hidden="true" className="h-2" />
        </div>
      </main>

      {/* ─── STICKY FOOTER (navigazione) ───────────────────────────────── */}
      <footer
        className="sticky bottom-0 z-20 bg-card/95 backdrop-blur border-t border-border px-3 py-3 md:px-4 md:py-4"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        data-testid="modulo2-footer"
      >
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            data-testid="wizard-back-button"
            className="min-h-[44px]"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
          </Button>
          {step < STEPS.length - 1 ? (
            <Button
              onClick={next}
              data-testid="wizard-next-button"
              className="min-h-[44px] flex-1 md:flex-none md:min-w-[170px]"
            >
              Continua <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={submitting || !form.consent_privacy}
              data-testid="wizard-submit-button"
              className="min-h-[44px] flex-1 md:flex-none md:min-w-[170px]"
            >
              {submitting ? 'Invio…' : 'Invia e prosegui'}
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
}
